import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Users, HeartHandshake, DollarSign } from "lucide-react";

const ACTION_IMG =
  "https://images.pexels.com/photos/31435403/pexels-photo-31435403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

export default function AboutJoseph() {
  const pillars = [
    {
      icon: <HeartHandshake className="h-4 w-4" />,
      h: "Personal",
      p: "You deal with real people from the first call to the final squeegee pass.",
    },
    {
      icon: <DollarSign className="h-4 w-4" />,
      h: "Transparent",
      p: "Clear per-window pricing on the website. Final number confirmed on-site.",
    },
    {
      icon: <ShieldCheck className="h-4 w-4" />,
      h: "Insured",
      p: "Window Shine LLC is fully insured for both residential and commercial jobs.",
    },
    {
      icon: <Users className="h-4 w-4" />,
      h: "Trained team",
      p: "A small, trusted crew led by Joseph — same standard, every property.",
    },
  ];

  return (
    <section
      id="about"
      className="bg-[#F9F9F6] py-24 md:py-32 border-t border-black/5"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5 relative"
        >
          <div className="absolute -left-6 -top-6 h-40 w-40 rounded-3xl bg-[#E8F1F2] -z-0" />
          <div className="relative overflow-hidden rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)]">
            <img
              src={ACTION_IMG}
              alt="Hand cleaning window with squeegee"
              className="h-[520px] w-full object-cover"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="md:col-span-7"
        >
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D] mb-4">
            Our story
          </div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111827] leading-tight">
            Rooted in San Angelo.
            <br />
            <span className="italic text-[#1A365D]">
              Built on trust.
            </span>
          </h2>
          <p className="mt-8 text-lg font-light leading-relaxed text-[#4B5563] max-w-2xl">
            Window Shine LLC started with a simple belief: your view
            matters, and the people cleaning it should treat it that way. What
            began with Joseph answering his own phone has grown into a small,
            insured team serving homes <em>and</em> businesses across San
            Angelo and the surrounding West Texas communities.
          </p>
          <p className="mt-5 text-lg font-light leading-relaxed text-[#4B5563] max-w-2xl">
            Joseph still leads every job and sets the standard — the same
            care, the same attention to tracks and corners, whether it&apos;s
            a storefront downtown or a kitchen looking onto the pasture.
            Straightforward pricing, insured work, and results you&apos;ll
            notice every time you look through the glass.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-4" data-testid="about-pillars">
            {pillars.map((b) => (
              <div
                key={b.h}
                className="rounded-2xl bg-white/70 border border-black/5 p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              >
                <div className="flex items-center gap-2 text-[#1A365D]">
                  {b.icon}
                  <span className="font-serif text-lg">{b.h}</span>
                </div>
                <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">
                  {b.p}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
